import React, { useState } from "react";
import { X, WhatsappLogo, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "@/components/ui/sonner";

// Preset outreach templates for WhatsApp. `{{name}}`, `{{role}}`, `{{recruiter}}`
// are substituted before sending. Kept concise for mobile-first reads.
const TEMPLATES = [
    {
        id: "shortlist",
        label: "Shortlist notification",
        text:
            "Hi {{name}}, this is {{recruiter}} from NaukriDaddy. Good news — we'd like to move forward with your application for the {{role}} role. Do you have 15 minutes this week for a quick call?",
    },
    {
        id: "interview_reminder",
        label: "Interview reminder",
        text:
            "Hi {{name}}, gentle reminder about your interview for the {{role}} role. Please join at the scheduled time. Ping me here if anything comes up. — {{recruiter}}",
    },
    {
        id: "docs_request",
        label: "Documents request",
        text:
            "Hi {{name}}, could you share your latest CV, current CTC & expected CTC, and notice period? Needed to move your application for {{role}} forward. Thanks! — {{recruiter}}",
    },
    {
        id: "offer_intro",
        label: "Offer discussion",
        text:
            "Hi {{name}}, we'd like to make you an offer for the {{role}} role. Are you free for a short call today to discuss the terms? — {{recruiter}}",
    },
    {
        id: "blank",
        label: "Blank message",
        text: "",
    },
];

function fill(text, ctx) {
    return (text || "")
        .replaceAll("{{name}}", ctx.name || "there")
        .replaceAll("{{role}}", ctx.role || "the role")
        .replaceAll("{{recruiter}}", ctx.recruiter || "");
}

function normalizePhone(raw) {
    if (!raw) return "";
    const digits = String(raw).replace(/\D/g, "");
    return digits;
}

export default function WhatsAppCandidateModal({ candidate, jobTitle, recruiterName, onClose, onSent }) {
    const [templateId, setTemplateId] = useState("shortlist");
    const [message, setMessage] = useState(() =>
        fill(TEMPLATES[0].text, {
            name: candidate.name,
            role: jobTitle || "",
            recruiter: recruiterName || "",
        }),
    );
    const phoneDigits = normalizePhone(candidate.phone);
    const hasPhone = phoneDigits.length >= 10;

    const applyTemplate = (id) => {
        setTemplateId(id);
        const t = TEMPLATES.find((x) => x.id === id);
        setMessage(
            fill(t?.text || "", {
                name: candidate.name,
                role: jobTitle || "",
                recruiter: recruiterName || "",
            }),
        );
    };

    // Build the wa.me URL once — used as the anchor href below. Popup blockers
    // and iframe embedders block programmatic window.open() but honour real
    // user-gesture anchor navigations, so we render this as an <a> tag.
    const waLink = phoneDigits
        ? `https://wa.me/${phoneDigits}?text=${encodeURIComponent(message)}`
        : "";

    const onSent_ = () => {
        toast.success("Opening WhatsApp", {
            description: `Message ready for ${candidate.name}. Hit Send in WhatsApp.`,
        });
        onSent?.();
        onClose?.();
    };

    return (
        <div
            className="fixed inset-0 z-50 bg-ink/40 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={onClose}
            data-testid="whatsapp-candidate-modal"
        >
            <div
                onClick={(e) => e.stopPropagation()}
                className="bg-white border border-[#E5E5E5] w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
                <div className="flex items-center justify-between p-5 border-b border-[#E5E5E5] sticky top-0 bg-white">
                    <div>
                        <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3]">
                            Outreach
                        </p>
                        <h2 className="font-heading text-2xl font-bold tracking-tight">
                            WhatsApp {candidate.name}
                        </h2>
                        <p className="text-xs text-[#525252] mt-1">
                            {hasPhone ? `+${phoneDigits}` : "No phone number on file"}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-[#F5F5F5]"
                        data-testid="close-whatsapp-modal"
                    >
                        <X size={18} weight="bold" />
                    </button>
                </div>

                {!hasPhone ? (
                    <div className="p-6 text-sm text-[#525252]">
                        Add a valid phone number on this candidate&apos;s record to send a WhatsApp message.
                    </div>
                ) : (
                    <div className="p-5 space-y-4">
                        <div>
                            <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1 block">
                                Template
                            </label>
                            <select
                                data-testid="wa-template-select"
                                value={templateId}
                                onChange={(e) => applyTemplate(e.target.value)}
                                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-cobalt"
                            >
                                {TEMPLATES.map((t) => (
                                    <option key={t.id} value={t.id}>{t.label}</option>
                                ))}
                            </select>
                        </div>

                        <div>
                            <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] mb-1 block">
                                Message ({message.length} chars)
                            </label>
                            <textarea
                                rows={8}
                                data-testid="wa-message-input"
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                placeholder="Type your message…"
                                className="w-full border border-[#E5E5E5] px-3 py-2.5 text-sm focus:outline-none focus:border-cobalt"
                            />
                            <p className="mt-1 text-[10px] text-[#A3A3A3]">
                                Opens in WhatsApp with the message pre-filled. You hit Send in WhatsApp.
                            </p>
                        </div>

                        <div className="flex gap-3 pt-1">
                            <button
                                type="button"
                                onClick={onClose}
                                className="border border-[#E5E5E5] hover:border-ink px-5 py-2.5 text-xs font-bold uppercase tracking-wider transition-colors"
                            >
                                Cancel
                            </button>
                            <a
                                href={waLink || "#"}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => {
                                    if (!message.trim() || !waLink) {
                                        e.preventDefault();
                                        return;
                                    }
                                    onSent_();
                                }}
                                data-testid="wa-open-button"
                                aria-disabled={!message.trim() || !waLink}
                                className={`flex-1 bg-[#25D366] hover:bg-[#1EBE5C] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-wider inline-flex items-center justify-center gap-2 transition-colors ${
                                    !message.trim() || !waLink ? "opacity-50 pointer-events-none" : ""
                                }`}
                            >
                                <WhatsappLogo size={14} weight="fill" />
                                Open WhatsApp <ArrowSquareOut size={12} weight="bold" />
                            </a>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
